<style scoped>
table.table-noborder > thead > tr > td,
table.table-noborder > tfoot > tr > td,
table.table-noborder > tbody > tr > td {
    border-top: none;
}
.table-responsive {
    /* To fix clipped column type popup */
    overflow: visible;
}
table input.form-control {
    width: 100%;
}

.data-source-banner {
    padding-bottom: 15px;
    margin-bottom: 15px;
    border-bottom: 1px solid #eeeeee;
}

.icon-unlink:before {
    color: #dc3545;
}

::v-deep(.icon-warning:before) {
    color: #e38e17;
    font-size: 1.2em;
}
.title-container {
    max-width: 80%;
}
.popper-vars {
    --popper-theme-border-color: #dee2e6;
    --popper-theme-border-width: 2px;
    --popper-theme-padding: 0.75rem;
}
.delete-column {
    border-color: #dc3545;
    border-radius: 0.2rem;
}
.column-edit-icon {
    position: absolute;
    top: 3px;
    right: 3px;
}
.close-schema-setting {
    border-color: #687078;
    border-radius: 0.2rem;
}
</style>
<template>
    <div>
        <LoadingContainer :promise="loadPromise">
            <div>
                <div class="clearfix data-source-banner">
                    <div class="d-flex mb-3">
                        <dl class="title-container" v-show="!editingDisplayName">
                            <dd class="d-block">
                                <h2 class="mb-3">
                                    {{ displayName }}
                                    <button class="btn btn-sm btn-bare icon icon-edit d-inline-block" :disabled="newlyCreated" @click="editingDisplayName = true" aria-label="Edit" />
                                </h2>
                            </dd>
                        </dl>
                        <div class="form-group title-container mr-3" v-show="editingDisplayName">
                            <label for="displayname" class="control-label font-weight-bold">{{ displayName }}</label>
                            <textarea id="datasourceDisplayName" class="form-control" v-model="newDisplayName" rows="3"></textarea>
                            <div class="pt-1">
                                <button class="btn btn-sm btn-primary d-inline-block" @click="saveDisplayName">Rename</button>
                                <AsyncOperationStatus :promise="editingDisplayNameSavePromise" />
                                <button class="btn btn-sm btn-bare d-inline-block" @click="editingDisplayName = false">Cancel</button>
                            </div>
                        </div>
                        <h2 class="mb-3">
                            <span class="text-muted" style="font-size: 15px; vertical-align: middle">({{ count }} rows)</span>
                        </h2>
                        <div class="ml-3">
                            <a class="btn btn-outline-danger btn-sm" tabindex="0" @click.prevent="deleteF()" id="deleteBtn"> Delete </a>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <ul class="nav nav-tabs mb-0" v-if="availableTestModes.length > 1 || testMode">
                            <li class="nav-item">
                                <a class="nav-link" tabindex="0" id="actualDataTab" data-toggle="tab" :class="{ active: !testMode, disabled: !canNonTestView }" @click.prevent="setTestMode(false)"> Actual Data </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" tabindex="0" id="testDataTab" data-toggle="tab" :disabled="noTestModeSegregation || newlyCreated" :class="{ active: testMode, disabled: noTestModeSegregation || newlyCreated }" @click.prevent="setTestMode(true)"> Test Data </a>
                            </li>
                        </ul>
                        <div class="ml-4 d-flex align-items-center" v-if="availableTestModes.length > 1 || testMode">
                            <button type="button" class="btn btn-outline-secondary btn-sm ml-2" tabindex="0" id="syncBtn" :disabled="noTestModeSegregation || newlyCreated" v-if="canTestEdit && canNonTestView" @click.prevent="doTestSync()">
                                <AsyncOperationStatus :promise="testSyncPromise" />
                                Sync Test Data
                            </button>
                            <label v-if="hasShowDataSourceTestModeSyncFeature || noTestModeSegregation" class="control-label d-flex align-items-center mb-0 ml-3" :class="{ disabled: !canNonTestEdit }">
                                &nbsp;Keep test data in sync&nbsp;
                                <span class="switch">
                                    <input type="checkbox" v-model="noTestModeSegregation" id="noTestModeSegregationToggle" name="noTestModeSegregation" :disabled="!canNonTestEdit" />
                                    <span class="slider"></span>
                                </span>
                                <AsyncOperationStatus class="ml-2" :promise="setTestSegregationPromise" />
                                <HelpIcon class="d-inline-flex ml-1" iconClass="icon-warning" v-if="noTestModeSegregation" text="Synced data sources don't allow add-in or activity updates." />
                            </label>
                        </div>
                    </div>
                </div>
                <div class="data-source-actions mb-3 d-inline-block" v-if="!isLinked">
                    <template v-if="canEdit">
                        <button type="button" id="saveBtn" class="btn btn-primary btn-sm" tabindex="0" @click.prevent="save()" :disabled="previewMode" :title="previewMode ? 'To update: export data source, edit it, and import it.' : undefined">
                            <AsyncOperationStatus :promise="saveDataPromise" />
                            Save<span v-if="previewMode">d</span>
                        </button>
                    </template>
                    <a class="btn btn-outline-secondary btn-sm ml-1" tabindex="0" :href="exportUrl">Export</a>
                    <template v-if="canEdit">
                        <a class="btn btn-outline-secondary btn-sm ml-1" tabindex="0" @click.prevent="showUploadFileDialog">Import</a>
                        <a class="btn btn-outline-secondary btn-sm ml-1" tabindex="0" @click.prevent="showExternalLinkDialog">Link to &hellip;</a>
                        <div class="btn btn-bare btn-sm ml-1" v-show="deleteStatus == 'in-progress' || deleteStatus == 'ok' || deleteStatus == 'fail'">
                            <LoadingWheel v-if="deleteStatus == 'in-progress'" style="position: relative; top: -2px; display: inline-block" :width="12"></LoadingWheel>
                            <span v-show="deleteStatus == 'ok'" style="color: green" class="icon icon-check"></span>
                            <span v-show="deleteStatus == 'fail'" style="color: red" class="icon icon-attention"></span>
                        </div>
                    </template>
                    <span class="text-muted font-italic ml-2 small" v-show="(datasource || {}).unsavedChanges">Unsaved changes.</span>
                </div>
                <div v-if="previewMode && !isLinked" class="data-source-preview float-right mb-3">
                    <p class="mb-0">
                        <strong>PREVIEW ONLY:</strong>
                        <span class="mr-2">Full table has {{ count }} rows.</span>
                        <span v-if="canEdit" class="font-italic small mt-1 mb-0 text-nowrap">To update: <a tabindex="0" :href="exportUrl">export</a> data source, edit it, and <a tabindex="0" @click.prevent="showUploadFileDialog">import</a> it.</span>
                    </p>
                </div>
                <div class="clearfix d-flex">
                    <div v-if="isLinked" class="d-flex align-items-center mb-3">
                        <p class="m-0 d-flex align-items-center data-source-preview mr-auto">
                            <strong class="badge badge-orange mr-2">{{ linkTypeLabel }}</strong>
                            {{ linkData!.linkHttpMethod }} {{ linkData!.linkUrl }}
                            <template v-if="canEdit">
                                <a class="btn btn-bare icon icon-edit ml-2 px-1" title="Edit Link" aria-label="Edit Link" tabindex="0" @click.prevent="showExternalLinkDialog" />
                                <a class="btn btn-bare icon icon-unlink px-1" id="unlinkBtn" title="Unlink" aria-label="Unlink" tabindex="0" @click.prevent="unlink()" />
                            </template>
                        </p>
                    </div>
                </div>
                <div class="table-responsive">
                    <table class="table table-bordered table-datasource">
                        <thead>
                            <tr style="background: #f8f8f8">
                                <template v-for="(key, index) in keys">
                                    <th style="position: relative" :class="{ 'bg-white': showPopper[index] }">
                                        <div class="input-group">
                                            <label class="sr-only" :for="idName + '_Header_' + index">Header Label {{ index }}</label>
                                            <span v-if="!canEdit || previewMode || testMode">{{ key }}</span>
                                            <template v-else>
                                                <Popper :show="showPopper[index]" class="popper-vars flex-grow-1 m-0 border-0" placement="bottom-start" @focus.capture="showPopper[index] = true" @blur.capture="showPopper[index] = false" @keydown.escape.capture="closeSchemaSettings(index)" offset-distance="0" offset-skid="-3" :tabindex="showPopper[index] ? 0 : -1">
                                                    <div>
                                                        <input type="text" class="form-control form-control-sm" autocomplete="off" v-model="keys[index]" :id="idName + '_Header_' + index" :title="key ?? undefined" placeholder="Header" aria-label="Edit Column" @keydown.shift.tab="showPopper[index] = false" @keypress.enter="enterInCell($event, -1, index)" @change="markChanged" />
                                                        <span class="icon icon-edit column-edit-icon" v-show="!showPopper[index]"></span>
                                                    </div>
                                                    <template #content>
                                                        <div class="container no-gutters p-0 nowrap">
                                                            <template v-if="hasDataSourceTypes">
                                                                <div class="d-flex align-items-baseline form-group col-12">
                                                                    <label class="control-label w-auto text-left mr-2" :for="idName + '_Type_' + index">Data Type</label>
                                                                    <select class="form-control-sm" v-model="keyTypes[index]" :id="idName + '_Type_' + index" :title="key ?? undefined" @change="markChanged">
                                                                        <option :value="value" v-for="(key, value) in columnTypeMapping">{{ key }}</option>
                                                                    </select>
                                                                </div>
                                                                <template v-if="keyTypes![index] == ColumnType.Text">
                                                                    <div class="d-flex justify-content-between form-group col-12">
                                                                        <label class="control-label w-auto text-left">
                                                                            Allow case-insensitive matches
                                                                            <span class="switch" @click="markChanged">
                                                                                <input type="checkbox" v-model="textColumnTypeInsensitive[index]" />
                                                                                <span class="slider"></span>
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                    <div class="d-flex justify-content-between form-group col-12">
                                                                        <label class="control-label w-auto text-left">
                                                                            Allow starts with matches
                                                                            <span class="switch" @click="markChanged">
                                                                                <input type="checkbox" v-model="textColumnTypeStartsWith[index]" />
                                                                                <span class="slider"></span>
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                </template>
                                                                <div class="d-flex justify-content-between form-group col-12">
                                                                    <label class="control-label w-auto text-left">
                                                                        Required
                                                                        <span class="switch" @click="markChanged">
                                                                            <input type="checkbox" v-model="columnRequired[index]" />
                                                                            <span class="slider"></span>
                                                                        </span>
                                                                    </label>
                                                                </div>
                                                            </template>
                                                            <div class="col-12 d-flex justify-content-between">
                                                                <button class="delete-column btn btn-outline-danger btn-sm w-auto" tabindex="0" @click.prevent="removeColumn(index)" :disabled="keys!.length <= 1">Remove</button>
                                                                <button @click="closeSchemaSettings(index)" type="button" class="close-schema-setting btn btn-outline-secondary btn-sm w-auto">Close</button>
                                                            </div>
                                                        </div>
                                                    </template>
                                                </Popper>
                                            </template>
                                        </div>
                                    </th>
                                </template>
                                <template v-if="canEdit && !previewMode && !testMode">
                                    <th style="vertical-align: middle; width: 45px" class="add-col">
                                        <button type="button" class="btn btn-outline-secondary btn-sm btn-add-col" title="Add Column" tabindex="0" @click="addColumn()">
                                            <span class="plus-sign" aria-hidden="true">+</span>
                                            <span class="sr-only">Add Column</span>
                                        </button>
                                    </th>
                                </template>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in values">
                                <td v-for="(value, columnIndex) in row">
                                    <span v-if="previewMode">{{ value }}</span>
                                    <input v-if="!previewMode" type="text" class="form-control form-control-sm" v-model="row[columnIndex]" :id="idName + '_Data_' + rowIndex + '_' + columnIndex" :aria-labelledby="idName + '_Header_' + columnIndex" @keypress.enter="enterInCell($event, rowIndex, columnIndex)" @change="markChanged" />
                                </td>
                                <td v-if="canEdit && !previewMode" class="add-col">
                                    <button type="button" class="btn btn-outline-secondary btn-sm btn-delete-row icon icon-trash" title="Delete Row" aria-label="Delete Row" tabindex="0" @click="removeRow(rowIndex)" :disabled="(values?.length ?? 0) <= 1" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="canEdit && !previewMode" class="add-new-row" :colspan="(keys || []).length + 1" style="text-align: center">
                    <button type="button" class="btn btn-outline-secondary btn-sm mt-2" tabindex="0" @click="addRow">Add Row</button>
                </div>
            </div>
        </LoadingContainer>
    </div>
</template>
<script lang="ts">
import { defineComponent, /*defineProps, defineEmits,*/ ref, watch, computed, nextTick, SetupContext, inject } from "vue";
import { asyncComputed } from "@vueuse/core";
import { throwIfNonSuccessStatus, fetchAuth, fetchXsrf } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import { useMainStore } from "@/mainStore";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import LoadingContainer from "@/lib/LoadingContainer.3.vue";
import HelpIcon from "@/lib/HelpIcon.3.vue";
import FileUploadModal from "./FileUploadModal.3.vue";
import ExternalLinkModal from "./ExternalLinkModal.3.vue";
import DatasourceReferencesModal from "./ReferencesModal.3.vue";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { LinkData } from "@cs-ts/link-data";
import { LinkTable } from "@cs/LinkTable";
import { ColumnType } from "@cs/ColumnType";
import { DatasourceModificationResult } from "@cs/DatasourceModificationResult";
import { showModalInjectKey, showModal } from "@/AppShell.3.vue.InjectKeys";
import { stringCoalesce } from "@/lib/whitespaceMethods";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
import zip from "@/lib/arrayZip";
import Popper from "vue3-popper";
import "vue-popperjs/dist/vue-popper.css";
interface Props {
    canNonTestEdit: boolean;
    canNonTestView: boolean;
    canTestEdit: boolean;
    canTestView: boolean;
    canEditProtected: boolean;
    name: string;
    displayName: string;
    newlyCreated: boolean;
}
// TODO - this should be from the CS bridge probably
const typeOptions = [
    {
        value: "Bulk",
        text: "Custom API",
        description: "Link to an API that refreshes daily.",
        help: null,
        protected: false,
    },
    {
        value: "Basic",
        text: "Custom API (Real-Time)",
        description: "Support data lookups in real-time.",
        help: null,
        protected: false,
    },
    {
        value: "Odata",
        text: "OData",
        description: "Use OData to link to an API.",
        help: `<p class="mt-2"><a href="https://www.odata.org/documentation/" target="_blank">View OData documentation</a></p>`,
        protected: false,
    },
    {
        value: "Socrata",
        text: "Data & Insights",
        description: "Integrate with Tyler Technologies' Data & Insights.",
        help: `
        <ol class="mt-2 pl-0 list-position-inside">
            <li><a href="https://support.socrata.com/hc/en-us/articles/202950718#:~:text=Finding%20a%20dataset%E2%80%99s%20OData%20URL" target="_blank">Copy the OData V4 URL</a> in Data & Insights to use as the Link URL.</li>
            <li><a href="https://support.socrata.com/hc/en-us/articles/360015776014-API-Keys" target="_blank">Create an API Key</a> in Data & Insights to use for Basic Auth credentials.</li>
        </ol>
        `,
        protected: false,
    },
    {
        value: "Gov2GoServiceEvent",
        text: "Gov2Go Subscription Events",
        description: "Manage Gov2Go Events for a Subscription.",
        //help: `<p class="mt-2">Set the Link URL to the service id from Gov2Go. Leave the username and password blank.</p>`,
        protected: true,
    },
];

interface Datasource {
    name: string;
    displayName: string;
    keys: (string | null)[];
    keyTypes: ColumnType[];
    keyLabels: (string | null)[];
    data: (string | null)[][];
    count: number;
    noTestModeSegregation?: null | boolean;
    linkData: LinkData | null;
    unsavedChanges?: boolean;
    newlyCreated?: boolean;
}
// TODO move elsewhere?
const columnTypeMapping = {
    [ColumnType.Date]: "Date",
    //[ColumnType.Boolean]: "Boolean", // TODO feature flag?
    [ColumnType.Number]: "Number",
    [ColumnType.Text]: "Text",
    //[ColumnType.Text_Lower]: "Text (insensitive) ($)",
    //[ColumnType.Text_Affix]: "Text (affix) ($$$)", // TODO feature flag?
    //[ColumnType.Text_Lower_Affix]: "Text (affix and insensitive) ($$$$)",
    //[ColumnType.Text_Prefix]: "Text (prefix) ($$)",
    //[ColumnType.Text_Lower_Prefix]: "Text (prefix and insensitive) ($$$)",
};
export default defineComponent({
    components: {
        LoadingWheel,
        LoadingContainer,
        AsyncOperationStatus,
        HelpIcon,
        Popper,
    },
    props: ["name", "displayName", "newlyCreated", "canTestEdit", "canNonTestEdit", "canTestView", "canNonTestView", "canEditProtected"],
    setup(props: Props, ctx: SetupContext) {
        //defineProps<Props>();
        const showModal = inject(showModalInjectKey) as showModal;

        const tab = ref("actual-data");
        const inited = ref(false);
        const loadPromise = ref<Promise<void>>(new Promise(() => {}));
        const setTestSegregationPromise = ref<Promise<void> | null>(null);
        const testSyncPromise = ref<Promise<void> | null>(null);
        const saveDataPromise = ref<Promise<void> | null>(null);
        const editingDisplayName = ref<boolean>(false);
        const editingDisplayNameSavePromise = ref<Promise<void> | null>(null); // TODO
        const newDisplayName = ref<string | undefined>(undefined);
        const deleteStatus = ref<null | string>(null); // TODO enum?
        const testMode = ref<boolean | null>(!(props.canNonTestView || props.canNonTestEdit));
        const datasource = ref<null | Datasource>(null);
        const textColumnTypeInsensitive = ref<boolean[]>([]);
        const textColumnTypeStartsWith = ref<boolean[]>([]);
        const columnRequired = ref<boolean[]>([]);
        const showPopper = ref<boolean[]>([]);
        const idName = computed(() => {
            return datasource.value?.name.replace(/ /g, "_");
        });
        const count = computed(() => {
            return datasource.value?.count;
        });
        const keys = computed(() => {
            return datasource.value?.keys ?? [];
        });
        const keyLabels = computed(() => {
            return datasource.value?.keyLabels ?? [];
        });
        const keyTypes = computed(() => {
            return datasource.value?.keyTypes ?? [];
        });
        const values = computed(() => {
            return datasource.value?.data ?? [];
        });
        const linkData = computed(() => {
            return datasource.value?.linkData;
        });
        const noTestModeSegregation = computed<boolean | undefined>({
            get() {
                return datasource.value?.noTestModeSegregation ?? undefined;
            },
            set(newValue: boolean | undefined) {
                datasource.value!.noTestModeSegregation = newValue;
            },
        });
        const previewMode = computed(() => {
            return datasource.value?.linkData != null || (datasource.value?.data == null ? 0 : datasource.value.data.length) < (datasource.value?.count ?? 0);
        });
        const isLinked = computed(() => {
            return datasource.value?.linkData != null;
        });
        const linkTypeLabel = computed(() => {
            return typeOptions.filter(i => i.value === linkData.value?.type).map(i => i.text)[0];
        });
        const mainStore = useMainStore();
        const exportUrl = asyncComputed(() => {
            return mainStore.getUrl("adminDataSourceExport", { tableName: props.name, testMode: testMode.value });
        });
        const availableTypeOptions = computed(() => {
            return typeOptions.filter(i => props.canEditProtected || !i.protected);
        });
        const canEdit = computed(() => {
            return (typeOptions.find(i => i.value == linkData.value?.type)?.protected ?? false) ? props.canEditProtected : testMode.value ? props.canTestEdit && !noTestModeSegregation.value : props.canNonTestEdit;
        });
        const canView = computed(() => {
            return testMode.value ? props.canTestView : props.canNonTestView;
        });
        const availableTestModes = computed(() => {
            const result = [];
            if (props.canNonTestEdit || props.canNonTestView) {
                result.push(false);
            }
            if (props.canTestEdit || props.canTestView) {
                result.push(true);
            }
            return result;
        });
        const hasShowDataSourceTestModeSyncFeature = asyncComputed(() => mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.ShowDataSourceTestModeSync.Id));
        const hasDataSourceTypes = asyncComputed(() => mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.PreferDatasourceElasticTypes.Id));

        //const emit = defineEmits(["unsaved-changes", "renamed", "saved", "delete"]);
        const emit = ctx.emit;

        const setArrayLength = <T,>(array: T[], length: number, newItem?: T) => {
            while (array.length > length) {
                array.pop();
            }
            while (array.length < length) {
                array.push(newItem as T);
            }
            return array;
        };
        const refresh = () => {
            if (props.newlyCreated) {
                datasource.value = datasource.value || {
                    name: props.name ?? "",
                    displayName: props.displayName ?? props.name ?? "",
                    count: 0,
                    keys: [""],
                    keyLabels: [""],
                    keyTypes: [ColumnType.Text],
                    data: [[""]],
                    noTestModeSegregation: hasShowDataSourceTestModeSyncFeature.value,
                    linkData: null,
                };
                markUnchanged();
                loadPromise.value = Promise.resolve();
                return;
            }
            datasource.value = null;
            return (loadPromise.value = (async () => {
                try {
                    const dataUrl = await mainStore.getUrl("adminDataSourceData", { id: props.name, testMode: testMode.value });
                    const resp = await fetchAuth(mainStore, dataUrl);
                    throwIfNonSuccessStatus(resp);
                    const table: LinkTable = await resp.json();
                    const columnTypeChecks = (type: ColumnType | undefined, required: boolean, index: number) => {
                        if (type == null) {
                            return ColumnType.Text;
                        }
                        textColumnTypeInsensitive.value[index] = [ColumnType.Text_Lower, ColumnType.Text_Lower_Prefix].includes(type);
                        textColumnTypeStartsWith.value[index] = [ColumnType.Text_Prefix, ColumnType.Text_Lower_Prefix].includes(type);
                        showPopper.value[index] = false;
                        columnRequired.value[index] = required;
                        return [ColumnType.Text, ColumnType.Text_Lower, ColumnType.Text_Prefix, ColumnType.Text_Lower_Prefix].includes(type!) ? ColumnType.Text : type;
                    };
                    datasource.value = {
                        name: table.Name!,
                        displayName: table.Name!,
                        count: table.DataCount,
                        keys: table.Schema!.length > 0 ? table.Schema.map(i => i.Name) : [],
                        keyLabels: table.Schema!.length > 0 ? table.Schema.map(i => stringCoalesce(i.Label, i.Name) ?? "") : [],
                        keyTypes: table.Schema!.length > 0 ? table.Schema.map((col, index) => columnTypeChecks(col.Type, col.Required, index)) : [],
                        data: table.Data!.map(i => i.map(j => (j as { Key: string; Value: string }).Value)),
                        noTestModeSegregation: table.NoTestModeSegregation,
                        linkData:
                            table.LinkData == null
                                ? null
                                : ({
                                      linkUrl: table.LinkData.LinkUrl,
                                      linkHttpMethod: table.LinkData.LinkHttpMethod,
                                      username: table.LinkData.Username,
                                      password: table.LinkData.Password,
                                      type: table.LinkData.Type,
                                      authType: table.LinkData.AuthType,
                                      cacheExpiresInSeconds: table.LinkData.CacheExpiresInSeconds,
                                      supportWriting: table.LinkData.SupportWriting,
                                      ignoreCacheControlHeader: table.LinkData.IgnoreCacheControlHeader,
                                      //supportFiltering: table.LinkData.SupportFiltering,
                                      //isLegacyApi: table.LinkData.IsLegacyApi,
                                  } as Partial<LinkData> as LinkData),
                    };
                    markUnchanged();
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            })());
        };
        const markChanged = () => {
            datasource.value!.unsavedChanges = true;
            emit("unsaved-changes", true);
        };
        const markUnchanged = () => {
            datasource.value!.unsavedChanges = false;
            emit("unsaved-changes", false);
        };
        const addRow = () => {
            datasource.value!.data.push(keys.value!.map(() => null));
            datasource.value!.count++;
            markChanged();
        };
        const removeRow = (rowIndex: number) => {
            datasource.value!.data.splice(rowIndex, 1);
            datasource.value!.count--;
            markChanged();
        };
        const addColumn = () => {
            datasource.value!.keys.push(null);
            datasource.value!.keyLabels.push(null);
            datasource.value!.keyTypes.push(ColumnType.Text);
            datasource.value!.data.forEach(i => i.push(null));
            markChanged();
        };
        const removeColumn = (columnIndex: number) => {
            datasource.value!.keys.splice(columnIndex, 1);
            datasource.value!.keyLabels.splice(columnIndex, 1);
            datasource.value!.keyTypes.splice(columnIndex, 1);
            datasource.value!.data.forEach(i => i.splice(columnIndex, 1));
            markChanged();
        };
        const setTestSegregation = () => {
            if (props.newlyCreated) return;
            return (setTestSegregationPromise.value = (async () => {
                const data = {
                    name: props.name,
                    noTestModeSegregation: noTestModeSegregation.value,
                };
                const formData = new FormData();
                for (let key of Object.keys(data) as (keyof typeof data)[]) {
                    formData.append(key, data[key]?.toString() ?? "");
                }
                const saveUrl = await mainStore.getUrl("adminDataSourceSetTestSegregation");
                const saveResponse = await fetchXsrf(mainStore, saveUrl, {
                    method: "POST",
                    body: formData,
                });
                throwIfNonSuccessStatus(saveResponse);
            })());
        };
        const setTestMode = (testModeI: boolean) => {
            testMode.value = testModeI;
        };
        const saveDisplayName = () => {
            editingDisplayNameSavePromise.value = (async () => {
                const formData = new FormData();
                formData.append("displayName", newDisplayName.value!);
                const response = await fetchXsrf(mainStore, await mainStore.getUrl("adminPageAlterDataSourceName", { tableKey: props.name }), {
                    method: "POST",
                    headers: await getXsftHeader(mainStore),
                    credentials: "same-origin",
                    body: formData,
                });
                throwIfNonSuccessStatus(response);
                editingDisplayName.value = false;
                emit("renamed", newDisplayName.value);
            })();
        };
        const save = async () => {
            if (previewMode.value) {
                alert("You can't save in preview mode. To update: export data source, edit it, and import it.");
                return;
            }
            const convertJsTypeToCsType = (type: ColumnType | null, index: number) => {
                if (type !== ColumnType.Text) {
                    return type;
                }
                const isInsensitive = textColumnTypeInsensitive.value[index];
                const hasStartsWith = textColumnTypeStartsWith.value[index];
                return isInsensitive && hasStartsWith ? ColumnType.Text_Lower_Prefix : isInsensitive ? ColumnType.Text_Lower : hasStartsWith ? ColumnType.Text_Prefix : ColumnType.Text;
            };
            const isColumnRequired = (index: number) => {
                return columnRequired.value[index];
            };
            const data = {
                Name: props.name,
                TestMode: testMode.value,
                Data: [] as { Key: string | null; Value: string | null }[][],
                NoTestModeSegregation: undefined as null | boolean | undefined,
                Schema: zip(keys.value, keyLabels.value, keyTypes.value).map(([k, l, t], index) => ({
                    Name: stringCoalesce(k, l),
                    Label: null, //TODO - proper implementation of label in form editor/import/export/etc - stringCoalesce(l, k),
                    Type: convertJsTypeToCsType(t, index),
                    Required: isColumnRequired(index),
                })),
            };
            if (props.newlyCreated) {
                data.NoTestModeSegregation = noTestModeSegregation.value;
            }
            datasource.value!.data.forEach((row, rowIndex) => {
                // if (rowIndex == this.datasource.data.length-1 && row.every(cell => cell == "")) {
                //    return;
                // }

                data.Data[rowIndex] = [];

                row.forEach((cell, colIndex) => {
                    data.Data[rowIndex][colIndex] = {
                        Key: keys.value![colIndex],
                        Value: cell,
                    };
                });
            });
            saveDataPromise.value = (async () => {
                const saveUrl = await mainStore.getUrl("adminDataSourceSave");
                const saveResponse = await fetchXsrf(mainStore, saveUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(await getXsftHeader(mainStore)),
                    },
                    credentials: "same-origin",
                    body: JSON.stringify(data),
                });
                throwIfNonSuccessStatus(saveResponse);
                var result = (await saveResponse.json()) as DatasourceModificationResult;
                if (result.success) {
                    setTimeout(() => {
                        refresh(); // allow icon to display before refreshing
                    }, 1500);
                    emit("saved");
                } else {
                    showModal(DatasourceReferencesModal, {
                        result: result,
                    });
                }
            })();
        };
        const deleteF = () => {
            emit("delete", (status: string) => {
                deleteStatus.value = status;
            });
        };
        const showUploadFileDialog = (event: Event) => {
            showModal(FileUploadModal, {
                name: props.name,
                testMode: testMode.value,
                canTestEdit: props.canTestEdit,
                noTestModeSegregation: noTestModeSegregation.value,
                confirmCallback: function (event: Datasource) {
                    const { data, keys, count } = event;
                    datasource.value!.data = data;
                    datasource.value!.keys = keys;
                    datasource.value!.keyLabels = setArrayLength(datasource.value!.keyLabels, keys.length);
                    datasource.value!.keyTypes = setArrayLength(datasource.value!.keyTypes, keys.length, ColumnType.Text);
                    textColumnTypeInsensitive.value = setArrayLength(textColumnTypeInsensitive.value, keys.length, false);
                    textColumnTypeStartsWith.value = setArrayLength(textColumnTypeStartsWith.value, keys.length, false);
                    columnRequired.value = setArrayLength(columnRequired.value, keys.length, false);
                    datasource.value!.count = count;
                    markUnchanged();
                    emit("saved");
                }.bind(this),
                triggerElement: event.target,
            });
        };
        const showExternalLinkDialog = (event: Event) => {
            const { linkUrl, authType, linkHttpMethod, username, password, type, supportWriting, cacheExpiresInSeconds, ignoreCacheControlHeader } = datasource.value!.linkData || {};
            showModal(ExternalLinkModal, {
                name: props.name,
                testMode: testMode.value,
                initialLinkUrl: linkUrl,
                initialLinkHttpMethod: linkHttpMethod,
                initialLinkUsername: username,
                initialLinkPassword: password,
                initialCacheExpiresInSeconds: cacheExpiresInSeconds,
                initialSupportWriting: supportWriting,
                initialIgnoreCacheControlHeader: ignoreCacheControlHeader,
                typeOptions: availableTypeOptions,
                initialType: type,
                initialAuthType: authType,
                canEdit: canEdit.value,
                confirmCallback: function (event: Datasource) {
                    const { data, keys, count, linkData } = event;
                    datasource.value!.data = data;
                    datasource.value!.keys = keys;
                    datasource.value!.keyLabels = setArrayLength(datasource.value!.keyLabels, keys.length);
                    datasource.value!.keyTypes = setArrayLength(datasource.value!.keyTypes, keys.length, ColumnType.Text);
                    textColumnTypeInsensitive.value = setArrayLength(textColumnTypeInsensitive.value, keys.length, false);
                    textColumnTypeStartsWith.value = setArrayLength(textColumnTypeStartsWith.value, keys.length, false);
                    columnRequired.value = setArrayLength(columnRequired.value, keys.length, false);
                    datasource.value!.count = count;
                    datasource.value!.linkData = linkData;
                    markUnchanged();
                    emit("saved");
                }.bind(this),
                triggerElement: event.target,
            });
        };
        const doTestSync = () => {
            if (!confirm("Override test data with actual data?")) {
                return;
            }
            return (testSyncPromise.value = (async () => {
                const data = new FormData();
                data.append("name", props.name ?? "");

                const saveUrl = await mainStore.getUrl("adminDataSourceTestSync");
                const saveResponse = await fetchXsrf(mainStore, saveUrl, {
                    method: "POST",
                    credentials: "same-origin",
                    body: data,
                });
                throwIfNonSuccessStatus(saveResponse);
                setTimeout(() => {
                    refresh(); // allow icon to display before refreshing
                }, 1500);
            })());
        };
        const unlink = () => {
            datasource.value!.linkData = null;
        };
        const enterInCell = (event: KeyboardEvent, rowIndex: number, columnIndex: number) => {
            event.preventDefault();
            const currentRow = (event.target as HTMLElement).closest("tr")!;
            let newRow = null;
            if (rowIndex == -1) {
                if (!event.shiftKey) {
                    newRow = currentRow.closest("table")!.querySelector("tbody tr:first-of-type");
                }
            } else {
                if (event.shiftKey) {
                    if (rowIndex == 0) {
                        newRow = currentRow.closest("table")!.querySelector("thead tr");
                    } else {
                        newRow = currentRow.previousElementSibling;
                    }
                } else {
                    newRow = currentRow.nextElementSibling;
                }
            }
            if (newRow != null) {
                const newElement = newRow.querySelector("td:nth-of-type(" + (columnIndex + 1) + ") input, th:nth-of-type(" + (columnIndex + 1) + ") input");
                (newElement as HTMLElement)?.focus();
            }
        };
        const closeSchemaSettings = (index: number) => {
            document.getElementById(`${idName.value}_Header_${index}`)?.focus();
            showPopper.value[index] = false;
        };

        watch(
            () => testMode.value,
            async (newVal, oldVal) => {
                if (inited.value && oldVal != null && newVal != null && newVal != oldVal) {
                    if (datasource.value && datasource.value.unsavedChanges && !confirm("You have unsaved changes. Continuing will lose them.")) {
                        testMode.value = null;
                        nextTick(() => {
                            testMode.value = oldVal;
                        });
                    } else {
                        await refresh();
                    }
                }
            },
        );

        watch(
            () => noTestModeSegregation.value,
            async (newVal, oldVal) => {
                if (inited.value && oldVal != null && newVal != null && newVal != oldVal) {
                    await setTestSegregation();
                    if (newVal) {
                        testMode.value = false;
                    }
                }
            },
        );

        watch(
            () => editingDisplayName.value,
            editingDisplayName => {
                if (editingDisplayName) {
                    newDisplayName.value = props.displayName; // todo?
                    editingDisplayNameSavePromise.value = null;
                }
            },
        );

        (async () => {
            await refresh();
            nextTick(() => {
                inited.value = true;
            });
        })();

        //console.log("P: " + JSON.stringify(props));

        return {
            //Refs
            inited,
            loadPromise,
            setTestSegregationPromise,
            testSyncPromise,
            saveDataPromise,
            editingDisplayName,
            editingDisplayNameSavePromise,
            newDisplayName,
            deleteStatus,
            testMode,
            datasource,
            textColumnTypeInsensitive,
            textColumnTypeStartsWith,
            columnRequired,
            showPopper,
            tab,

            columnTypeMapping,

            //computed
            canEdit,
            canView,
            availableTestModes,
            idName,
            count,
            keys,
            keyLabels,
            keyTypes,
            values,
            linkData,
            noTestModeSegregation,
            previewMode,
            isLinked,
            linkTypeLabel,
            exportUrl,
            hasShowDataSourceTestModeSyncFeature,
            hasDataSourceTypes,

            //methods
            refresh,
            markChanged,
            markUnchanged,
            addRow,
            removeRow,
            addColumn,
            removeColumn,
            setTestSegregation,
            setTestMode,
            saveDisplayName,
            save,
            deleteF,
            showUploadFileDialog,
            showExternalLinkDialog,
            doTestSync,
            unlink,
            enterInCell,
            closeSchemaSettings,

            // Constants
            typeOptions,
            ColumnType,
        };
    },
});
</script>
